import type { Theme } from '@emotion/react';
import { useTheme } from '@emotion/react';
import {
  AwarenessBanner,
  Button,
  CardItemBreed,
  CardItemBreeder,
  CardItemLitter,
  Container,
  DetailList,
  HeaderProfile,
  InfoBox,
  Section,
  SectionCardItemSlider,
  SectionTitle,
  Slideshow,
  StoreBanner,
  TranslatableContent,
  TranslateButton,
  VetAdviceBanner,
  WhatsappButton,
} from '@presentation/components';
import { ConnectionTag } from '@presentation/components/ContactInfo/components/ConnectionTag';
import { FixedButtonBanner } from '@presentation/components/FixedButtonBanner';
import { useTranslation } from '@presentation/utils';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { FaCalendar, FaCheck, FaTimes } from 'react-icons/fa';
import { IoMdHeartDislike } from 'react-icons/io';
import { RiHeartAddFill } from 'react-icons/ri';
import { TbShare } from 'react-icons/tb';
import ReadMoreReact from 'read-more-react';
import * as Styled from './styled';
import type { LitterPageInfoDetailIcon, LitterPageProps } from './types';

export * from './types';

const DetailIconComponent = (theme: Theme): Record<LitterPageInfoDetailIcon, ReactNode> => ({
  cross: <FaTimes css={Styled.infoDetailIconStyle} color={theme.color.red.default} />,
  check: <FaCheck css={Styled.infoDetailIconStyle} color={theme.color.green.default} />,
  calendar: <FaCalendar css={Styled.infoDetailIconStyle} color={theme.color.blue.default} />,
});

export const LitterPage: FC<LitterPageProps> = (props) => {
  const {
    description,
    breedName,
    details,
    breedCard,
    header,
    parents = [],
    contactInfo,
    slideshow,
    onWishlistClick,
    isWishlisted = true,
    wishlistLoading = false,
    showWishlist = true,
    warning,
    onOpenBuyerChecklist,
    litterItems = [],
    breederItems = [],
    litterItemsLoading = false,
    breederItemsLoading = false,
    moreBreedersLink,
    moreLittersLink,
    showApplyButton,
    onSocialShareClick,
    onWhatsappClick,
    enableWhatsAppContact = false,
  } = props;

  const { translate } = useTranslation();
  const theme = useTheme();

  const litterCards = useMemo(() => {
    if (litterItemsLoading) {
      return Array(4)
        .fill(undefined)
        .map((_, index) => (
          <CardItemLitter
            style={{ height: 327.5 }}
            key={index}
            loading
            breed=""
            price=""
            location=""
          />
        ));
    }
    return litterItems.map((item, index) => (
      <CardItemLitter style={{ height: 327.5 }} key={index} {...item} />
    ));
  }, [litterItems, litterItemsLoading]);

  const breederCards = useMemo(() => {
    if (breederItemsLoading) {
      return Array(4)
        .fill(undefined)
        .map((_, index) => <CardItemBreeder style={{ height: 375 }} key={index} loading />);
    }
    return breederItems.map((item, index) => (
      <CardItemBreeder style={{ height: 375 }} key={index} {...item} />
    ));
  }, [breederItems, breederItemsLoading]);

  return (
    <>
      <HeaderProfile {...header}>
        <Styled.HeaderButtons>
          {showWishlist && (
            <Styled.WishlistWrapper
              onClick={(event) => {
                if (wishlistLoading) return;
                onWishlistClick?.(event);
              }}
              loading={wishlistLoading}
            >
              {isWishlisted ? (
                <>
                  <div>{translate('common.buttons.unfavorite')}</div>
                  {wishlistLoading ? <Styled.WishlistLoading /> : <IoMdHeartDislike />}
                </>
              ) : (
                <>
                  <div>{translate('common.buttons.favorite')}</div>
                  {wishlistLoading ? <Styled.WishlistLoading /> : <RiHeartAddFill />}
                </>
              )}
            </Styled.WishlistWrapper>
          )}

          <Button
            shrink
            color="dark"
            iconLeft={<TbShare />}
            size="small"
            onClick={onSocialShareClick}
          >
            {translate('common.buttons.share')}
          </Button>
        </Styled.HeaderButtons>
      </HeaderProfile>
      <Container>
        <Styled.TopWrapper>
          {warning && (
            <Styled.WarningWrapper>
              <InfoBox>{warning}</InfoBox>
            </Styled.WarningWrapper>
          )}
          <Styled.TopFlexWrapper>
            <Styled.TopSlideshowWrapper>
              {!!slideshow && <Slideshow aspectRatio={4 / 3} {...slideshow} />}
            </Styled.TopSlideshowWrapper>
            <Styled.TopDetailsWrapper>
              <DetailList
                items={details.map((detail) => ({
                  label: detail.label,
                  value: (
                    <>
                      <span>{detail.value ?? '-'}</span>
                      {!!detail.icon && <>{DetailIconComponent(theme)[detail.icon]}</>}
                    </>
                  ),
                }))}
              />
              <Styled.TopButtonsContainer>
                {showApplyButton ? (
                  <Button
                    key="apply"
                    color="blue"
                    data-from="litter"
                    onClick={contactInfo?.onApplyClick}
                  >
                    {translate('common.buttons.apply')}
                  </Button>
                ) : null}
                {enableWhatsAppContact ? (
                  <WhatsappButton outlined={false} onClick={onWhatsappClick} size="medium" />
                ) : null}
                <Button color="blue" outlined onClick={contactInfo?.onClick}>
                  {translate('common.buttons.viewProfile')}
                </Button>
              </Styled.TopButtonsContainer>
            </Styled.TopDetailsWrapper>
          </Styled.TopFlexWrapper>
        </Styled.TopWrapper>

        <Styled.StoreBannerContainer>
          <StoreBanner source="litter" theme="lightBlue" />
        </Styled.StoreBannerContainer>

        {!!parents.length && (
          <Section>
            <SectionTitle>Parents</SectionTitle>
            <Styled.ParentContainer>
              {parents.map((parent, index) => (
                <Styled.ParentCard key={index}>
                  <Styled.ParentImage
                    src={parent.image}
                    alt=""
                    onClick={parent.onImageClick}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  <div>
                    <Styled.ParentTitle>
                      {translate(`enum.parentType.${parent.type}`)}
                    </Styled.ParentTitle>
                    <Styled.ParentSubtitle>{parent.name}</Styled.ParentSubtitle>
                    {!parent.pedigreeNumber && parent.pedigreeType && (
                      <Styled.ParentLabel>
                        <strong>{parent.pedigreeType}</strong>
                      </Styled.ParentLabel>
                    )}
                    {parent.pedigreeNumber && parent.pedigreeType && (
                      <Styled.ParentLabel>
                        <strong>{parent.pedigreeType}:</strong> {parent.pedigreeNumber}
                      </Styled.ParentLabel>
                    )}
                    {!!parent.age && (
                      <Styled.ParentLabel>
                        <strong>{translate('glossary.age')}:</strong> {parent.age}
                      </Styled.ParentLabel>
                    )}
                    {!!parent.titles && (
                      <Styled.ParentLabel>
                        <strong>{translate('glossary.titles')}:</strong>
                        <ReadMoreReact
                          text={parent.titles}
                          min={40}
                          ideal={40}
                          max={9999}
                          readMoreText={`${translate('common.buttons.seeMore')}...`}
                        />
                      </Styled.ParentLabel>
                    )}
                    {!!parent.testResults && (
                      <Styled.ParentLabel>
                        <strong>{translate('common.inputs.testResults.label')}:</strong>
                        <ReadMoreReact
                          text={parent.testResults}
                          min={40}
                          ideal={40}
                          max={9999}
                          readMoreText={`${translate('common.buttons.seeMore')}...`}
                        />
                      </Styled.ParentLabel>
                    )}
                  </div>
                </Styled.ParentCard>
              ))}
            </Styled.ParentContainer>
          </Section>
        )}

        {!!breedCard && (
          <Section>
            <Styled.DescriptionContainer>
              <div>
                <SectionTitle>{translate('glossary.breed')}</SectionTitle>
                <Styled.BreedCardWrapper>
                  <CardItemBreed {...breedCard} />
                </Styled.BreedCardWrapper>
              </div>
              <div>
                <Styled.TranslatableDescription>
                  <SectionTitle>{translate('glossary.description')}</SectionTitle>
                  <TranslateButton source="litter" />
                </Styled.TranslatableDescription>

                <Section>
                  <TranslatableContent content={description} />
                </Section>
              </div>
            </Styled.DescriptionContainer>
          </Section>
        )}

        <Section>
          <Styled.ContactContainer>
            <Styled.ContactImgWrapper>
              <Styled.ContactImg
                src={contactInfo?.profileImage}
                onContextMenu={(e) => e.preventDefault()}
              />
              {!!contactInfo?.lastConnectedType && (
                <ConnectionTag type={contactInfo.lastConnectedType} />
              )}
              <Styled.ContactLink onClick={contactInfo?.onClick}>
                {translate('common.buttons.viewProfile')}
                {' >'}
              </Styled.ContactLink>
            </Styled.ContactImgWrapper>
            <Styled.ContactDetailList
              items={(contactInfo?.values ?? []).map((value) => ({
                label: value.label,
                value: value.value ?? '-',
              }))}
            />
          </Styled.ContactContainer>
        </Section>
      </Container>

      <Styled.AwarenessBannerWrapper>
        <AwarenessBanner onClick={onOpenBuyerChecklist} />
      </Styled.AwarenessBannerWrapper>
      <Container>
        <Styled.VerticalMargin>
          <SectionCardItemSlider
            title={translate('common.sections.moreLittersFromBreed.title').replace(
              '$breed',
              breedName,
            )}
            loading={litterItemsLoading}
            items={litterCards}
            showMoreLink={moreLittersLink}
          />
        </Styled.VerticalMargin>

        <Styled.VerticalMargin>
          <SectionCardItemSlider
            title={translate('common.sections.moreBreedersFromBreed.title').replace(
              '$breed',
              breedName,
            )}
            loading={breederItemsLoading}
            items={breederCards}
            showMoreLink={moreBreedersLink}
          />
        </Styled.VerticalMargin>
      </Container>

      {showApplyButton && (
        <FixedButtonBanner onClick={contactInfo?.onApplyClick} from="litter" type="application" />
      )}

      {enableWhatsAppContact && (
        <FixedButtonBanner onClick={onWhatsappClick} from="litter" type="whatsapp" />
      )}
    </>
  );
};
